import Tag from '@/components/ui/tag';
import { cn } from '@/lib/cn';
import { type FilterSettings, type TemplateCommunitySortBy } from '@/types';
import { Button } from '@heidi/design-system/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { useIsExperimentalDesignSystemForms } from '@heidi/design-system/hooks/use-is-experimental-design-system-forms';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@heidi/design-system/select';
import { Separator } from '@heidi/design-system/separator';
import { type Region, type TemplateCategory } from '@heidi/schema/ml-scribe';
import { SelectValue } from '@radix-ui/react-select';
import { CommandList } from 'cmdk';
import { default as Fuse } from 'fuse.js';
import { Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  getFilterEmptyMessage,
  getFilterSearchPlaceholder,
  getFilterSelectPlaceholder,
} from './filter.messages';

type Props = {
  icon: React.ReactNode;
  title: string;
  values: string[];
  labels: string[];
  value: string;
  useCommand: boolean;
  onChange: (
    value: FilterSettings | TemplateCategory | Region | TemplateCommunitySortBy | string
  ) => void;
  name: string;
};

export const FILTER_RESULTS = {
  MATCH: 1,
  NO_MATCH: 0,
} as const;

const Filter = ({
  icon,
  title,
  values,
  value,
  labels,
  useCommand,
  onChange,
  name,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const commandListRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const searchPlaceholder = getFilterSearchPlaceholder(intl, name);
  const selectPlaceholder = getFilterSelectPlaceholder(intl, name);

  const handleFilter = useCallback(
    (value: string, search: string) => {
      if (!search) return 1;

      const fuse = new Fuse(labels, {
        threshold: 0.6,
      });
      const results = fuse.search(search);

      const matchIndex = results.findIndex(
        (result) => result.item.toLowerCase() === value.toLowerCase()
      );

      if (matchIndex < 0) return 0;

      return results.length - matchIndex;
    },
    [labels]
  );

  useEffect(() => {
    if (commandListRef.current) {
      setTimeout(() => {
        commandListRef.current?.scrollTo({ top: 0 });
      }, 1);
    }
  }, [searchValue]);

  return (
    <>
      {useCommand ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size={isExperimentalForms ? 'md' : 'lg'}
              disableAutomaticSvgSizing
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="justify-between bg-surface"
            >
              <div className="flex w-fit items-center gap-2">
                {icon}
                <div className="text-sm font-normal">{title}</div>
                <Separator className="h-4" orientation="vertical" />
                <Tag variant="solid" className="whitespace-nowrap text-xs">
                  {value
                    ? labels[values.findIndex((val) => val === value)]
                    : selectPlaceholder}
                </Tag>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="overflow-auto p-0">
            <Command filter={handleFilter}>
              <CommandInput
                placeholder={searchPlaceholder}
                value={searchValue}
                onValueChange={setSearchValue}
              />
              <CommandList ref={commandListRef} className="max-h-80 overflow-y-auto">
                <CommandEmpty>{getFilterEmptyMessage(intl, name)}</CommandEmpty>
                <CommandGroup>
                  {labels.map((label, i) => (
                    <CommandItem
                      key={label}
                      value={label}
                      onSelect={() => {
                        onChange(values[i]!);
                        setOpen(false);
                      }}
                      className="flex items-center gap-2"
                    >
                      <span>{label}</span>
                      <Check
                        size={16}
                        className={cn(
                          'ml-auto min-h-4 min-w-4',
                          values.findIndex((val) => value === val) === i
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : (
        <Select onValueChange={onChange} value={value}>
          <SelectTrigger
            hideSelectIcon={true}
            className="w-fit transition hover:bg-background-secondary"
          >
            <div className="flex w-fit items-center gap-2">
              {icon}
              <div>{title}</div>
              <Separator className="h-4" orientation="vertical" />
              <Tag variant="solid" className="whitespace-nowrap text-xs">
                <SelectValue />
              </Tag>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {values.map((value, i) => (
                <SelectItem key={value} value={value}>
                  {labels[i]}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    </>
  );
};

export default Filter;
